import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import {
    IAudioData,
    IAudioDataStatistic,
    ISpeakingEvent,
    ISpeakingAudioData,
    IAudioEmotionEvent,
} from '../types/AudioData';
import { ISessionAnalytics } from '../types/AnalyticsData';
import { AudioService } from '../services';
import { IEngagementEmotion } from '../types/Config';

export default class AudioStore {
    activeSessionRatingData: ISessionAnalytics | null = null;

    audioDataLoading: boolean = false;

    audioStatisticDataLoading: boolean = false;

    sessionRatingDataLoading: boolean = false;

    speakingData: ISpeakingAudioData | null = null;

    audioStatistic: IAudioDataStatistic | null = null;

    bothSpeaking: ISpeakingEvent[] | null = null;

    fmmData: IAudioEmotionEvent[] | null = null;

    fmmSummary: number[] | null = null;

    fmmSummaryLoading: boolean = false;

    fmmDataLoading: boolean = false;

    error: any;

    private audioService: typeof AudioService;

    constructor() {
        this.audioService = AudioService;
        makeAutoObservable(this);
    }

    private setSessionRatingData(ratingData: ISessionAnalytics | null): void {
        this.activeSessionRatingData = ratingData;
    }

    private setFmmData(fmmData: IAudioEmotionEvent[] | null): void {
        this.fmmData = fmmData;
    }

    private setFmmDataLoading(value: boolean): void {
        this.fmmDataLoading = value;
    }

    private setFmmSummary(fmmData: number[] | null): void {
        this.fmmSummary = fmmData;
    }

    private setFmmSummaryLoading(value: boolean): void {
        this.fmmSummaryLoading = value;
    }

    private setAudioStatisticData(
        audioStatistic: IAudioDataStatistic | null,
    ): void {
        this.audioStatistic = audioStatistic;
    }

    private setBothSpeakingData(bothSpeaking: ISpeakingEvent[] | null): void {
        this.bothSpeaking = bothSpeaking;
    }

    private setSpeakingData(speakingData: ISpeakingAudioData | null): void {
        this.speakingData = speakingData;
    }

    private setSessionRatingDataLoading(value: boolean): void {
        this.sessionRatingDataLoading = value;
    }

    private setAudioDataLoading(value: boolean): void {
        this.audioDataLoading = value;
    }

    private setAudioStatisticDataLoading(value: boolean): void {
        this.audioStatisticDataLoading = value;
    }

    private setError(error: any): void {
        // eslint-disable-next-line no-console
        console.error(error);
        this.error = error;
        toast.error(error.message);
    }

    clearAllData(): void {
        this.setSessionRatingData(null);
        this.setSpeakingData(null);
        this.setBothSpeakingData(null);
        this.setAudioStatisticData(null);
        this.setFmmData(null);
        this.setFmmSummary(null);
    }

    async fetchAudioData(
        userEmail: string,
        sessionName: string,
    ): Promise<void> {
        this.setSpeakingData(null);
        this.setAudioStatisticData(null);
        this.setBothSpeakingData(null);
        try {
            this.setAudioDataLoading(true);
            const data: IAudioData = await this.audioService.fetchAudioData(
                userEmail,
                sessionName,
            );
            this.setAudioDataLoading(false);
            const newTurnTakingData: ISpeakingAudioData = {
                simSpecialistSpeaking: data.simSpecialistSpeaking,
                learnerSpeaking: data.learnerSpeaking,
            };
            this.setSpeakingData(newTurnTakingData);
            this.setBothSpeakingData(data.bothSpeaking);
        } catch (error) {
            this.setError(error);
            this.setAudioDataLoading(false);
        }
    }

    async fetchAudioStatisticData(
        userEmail: string,
        sessionName: string,
        fromSeconds?: number,
        toSeconds?: number,
    ): Promise<void> {
        this.setAudioStatisticData(null);
        try {
            this.setAudioStatisticDataLoading(true);
            const data: IAudioDataStatistic =
                await this.audioService.fetchAudioStatistic(
                    userEmail,
                    sessionName,
                    fromSeconds,
                    toSeconds,
                );
            this.setAudioStatisticDataLoading(false);
            this.setAudioStatisticData(data);
        } catch (error) {
            this.setError(error);
            this.setAudioStatisticDataLoading(false);
        }
    }

    async fetchSessionRatingAnalyticsData(
        userEmail: string,
        sessionName: string,
    ): Promise<void> {
        try {
            this.setSessionRatingDataLoading(true);
            const data: ISessionAnalytics =
                await this.audioService.fetchSessionAnalytics(
                    userEmail,
                    sessionName,
                );
            this.setSessionRatingData(data);
            this.setSessionRatingDataLoading(false);
        } catch (error) {
            this.setSessionRatingDataLoading(false);
            this.setError(error);
        }
    }

    async fetchFmmData(
        userEmail: string,
        sessionName: string,
        levels?: number[],
        avrSeconds?: number,
        emotionsAndWeight?: IEngagementEmotion[],
    ): Promise<void> {
        try {
            this.setFmmDataLoading(true);
            const data: IAudioEmotionEvent[] =
                await this.audioService.fetchFmmData(
                    userEmail,
                    sessionName,
                    levels,
                    avrSeconds,
                    emotionsAndWeight,
                );
            this.setFmmData(data);
            this.setFmmDataLoading(false);
        } catch (error) {
            this.setFmmDataLoading(false);
            this.setError(error);
        }
    }

    async fetchFmmSummary(
        userEmail: string,
        sessionName: string,
        interval?: {
            from: number;
            to: number;
        },
        levels?: number[],
        avrSeconds?: number,
        emotionsAndWeight?: IEngagementEmotion[],
    ): Promise<void> {
        try {
            this.setFmmSummaryLoading(true);
            const data: number[] = await this.audioService.fetchFmmSummary(
                userEmail,
                sessionName,
                interval,
                levels,
                avrSeconds,
                emotionsAndWeight,
            );
            this.setFmmSummary(data);
            this.setFmmSummaryLoading(false);
        } catch (error) {
            this.setFmmSummaryLoading(false);
            this.setError(error);
        }
    }
}
